import React, { useState } from 'react';

const ShareButton = ({ 
  url, 
  title = 'Share Link',
  size = 'normal', // 'small', 'normal', 'large'
  variant = 'button', // 'button', 'icon', 'text'
  style = {}
}) => {
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleShare = async (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    const shareUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    
    // Try native share first (mobile)
    if (navigator.share && /mobile|android|iphone|ipad/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: title,
          url: shareUrl
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== 'AbortError') {
          console.log('Share failed, using clipboard fallback');
        }
      }
    }
    
    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setShowTooltip(true);
      setTimeout(() => {
        setCopied(false);
        setShowTooltip(false);
      }, 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setShowTooltip(true);
        setTimeout(() => {
          setCopied(false);
          setShowTooltip(false);
        }, 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const sizeStyles = {
    small: { padding: '4px 8px', fontSize: '12px' },
    normal: { padding: '8px 16px', fontSize: '14px' },
    large: { padding: '12px 24px', fontSize: '16px' }
  };

  const baseButtonStyle = {
    background: copied ? '#28a745' : '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    ...sizeStyles[size],
    ...style
  };

  const iconOnlyStyle = {
    ...baseButtonStyle,
    padding: size === 'small' ? '6px' : size === 'large' ? '12px' : '8px',
    borderRadius: '50%',
    minWidth: 'auto',
    minHeight: 'auto'
  };

  const textStyle = {
    background: 'none',
    border: 'none',
    color: copied ? '#48bb78' : '#2563eb',
    cursor: 'pointer',
    padding: '4px 8px',
    fontSize: sizeStyles[size].fontSize,
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    position: 'relative',
    textDecoration: 'underline',
    ...style
  };

  const tooltipStyle = {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#2d3748',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    marginBottom: '8px',
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    opacity: showTooltip ? 1 : 0,
    transition: 'opacity 0.2s ease',
    pointerEvents: 'none'
  };

  const getButtonStyle = () => {
    switch (variant) {
      case 'icon':
        return iconOnlyStyle;
      case 'text':
        return textStyle;
      default:
        return baseButtonStyle;
    }
  };

  const ShareIcon = () => (
    <svg 
      width={size === 'small' ? 14 : size === 'large' ? 20 : 16} 
      height={size === 'small' ? 14 : size === 'large' ? 20 : 16} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <circle cx="18" cy="5" r="3"/>
      <circle cx="6" cy="12" r="3"/>
      <circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg 
      width={size === 'small' ? 14 : size === 'large' ? 20 : 16} 
      height={size === 'small' ? 14 : size === 'large' ? 20 : 16} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );

  return (
    <button
      onClick={handleShare}
      style={getButtonStyle()}
      title={copied ? 'Copied!' : 'Copy link to share'}
      onMouseEnter={(e) => {
        if (variant === 'button') {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
        }
      }}
      onMouseLeave={(e) => {
        if (variant === 'button') {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
        }
      }}
    >
      <span style={tooltipStyle}>
        {copied ? '✓ Link copied!' : 'Copy link'}
      </span>
      {copied ? <CheckIcon /> : <ShareIcon />}
      {variant === 'button' && (copied ? 'Copied!' : 'Share')}
      {variant === 'text' && (copied ? 'Copied!' : 'Share Link')}
    </button>
  );
};

export default ShareButton;
